import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

export default class extends Controller {
  static targets = ["form"]
  static values = {
    interval: { type: Number, default: 30000 }, // 30 seconds
    debounceDelay: { type: Number, default: 2000 }, // 2 seconds
    url: String,
    applicationId: String
  }

  connect() {
    this.isConnected = true
    this.debounceTimer = null
    this.intervalTimer = null
    this.lastSaveTime = Date.now()
    this.isSaving = false
    this.hasChanges = false

    // Start interval-based auto-save
    this.startAutoSaveInterval()

    // Listen for form changes (also re-attached via formTargetConnected on turbo frame renders)
    this.setupFormChangeListeners()

    // Save on page unload (beforeunload)
    this.boundBeforeUnload = this.handleBeforeUnload.bind(this)
    window.addEventListener('beforeunload', this.boundBeforeUnload)

    // Save on visibility change (tab switching)
    this.boundVisibilityChange = this.handleVisibilityChange.bind(this)
    document.addEventListener('visibilitychange', this.boundVisibilityChange)
  }

  formTargetConnected() {
    this.setupFormChangeListeners()
  }

  disconnect() {
    this.isConnected = false

    // Clear timers
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    if (this.intervalTimer) {
      clearInterval(this.intervalTimer)
    }

    // Remove event listeners
    window.removeEventListener('beforeunload', this.boundBeforeUnload)
    document.removeEventListener('visibilitychange', this.boundVisibilityChange)
  }

  setupFormChangeListeners() {
    const form = this.#findForm()
    if (!form) {
      console.warn("No form found for auto-save")
      return
    }

    // Track changes on input (but don't save yet)
    form.addEventListener('input', this.trackChange.bind(this))

    // Save on focusout (when user exits a field) - focusout bubbles unlike blur
    form.addEventListener('focusout', this.handleFieldExit.bind(this))

    // Save on change (for selects/checkboxes)
    form.addEventListener('change', this.handleFieldChange.bind(this))

    // Special handling for sliders (range inputs) - save immediately on release
    const sliders = form.querySelectorAll('input[type="range"]')
    sliders.forEach(slider => {
      slider.addEventListener('mouseup', () => this.saveNow())
      slider.addEventListener('touchend', () => this.saveNow())
    })
  }

  trackChange(event) {
    // Just mark that we have changes, don't trigger save
    this.hasChanges = true
  }

  handleFieldExit(event) {
    if (this.isSaving) return
    if (!this.hasChanges) return

    const target = event.target
    const isTextField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA'

    if (isTextField) {
      this.saveNow()
    }
  }

  handleFieldChange(event) {
    // Save immediately for select, checkbox, radio changes
    if (this.isSaving) return

    this.hasChanges = true

    const target = event.target
    const isSelect = target.tagName === 'SELECT'
    const isCheckOrRadio = target.type === 'checkbox' || target.type === 'radio'
    const isHidden = target.type === 'hidden'

    if (isSelect || isCheckOrRadio || isHidden) {
      this.saveNow()
    }
  }

  startAutoSaveInterval() {
    if (this.intervalTimer) {
      clearInterval(this.intervalTimer)
    }

    this.intervalTimer = setInterval(() => {
      if (this.isConnected && !this.isSaving && this.hasChanges) {
        this.saveNow()
      }
    }, this.intervalValue)
  }

  debounceAutoSave() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    this.debounceTimer = setTimeout(() => {
      if (this.isConnected && this.hasChanges) {
        this.saveNow()
      }
    }, this.debounceDelayValue)
  }

  // Returns true if a phone field has incomplete digits (started typing but < 10)
  #hasIncompletePhone() {
    const form = this.#findForm()
    if (!form) return false

    const phoneInput = form.querySelector('input[name*="applicant_phone"]')
    if (!phoneInput) return false

    const digits = phoneInput.value.replace(/\D/g, '')
    return digits.length > 0 && digits.length < 10
  }

  #hasIncompleteSSN() {
    const form = this.#findForm()
    if (!form) return false

    const ssnInput = form.querySelector('input[name*="applicant_ssn"]')
    if (!ssnInput) return false

    const digits = ssnInput.value.replace(/\D/g, '')
    return digits.length > 0 && digits.length < 9
  }

  // Uses @rails/request.js directly to bypass Turbo Frame scoping.
  // The form lives inside a Turbo Frame, but auto-save returns 204 (no content)
  // which would cause a FrameMissingError if submitted through Turbo.
  async saveNow() {
    if (this.isSaving || !this.hasChanges) return
    if (this.#hasIncompletePhone()) return
    if (this.#hasIncompleteSSN()) return

    const form = this.#findForm()
    if (!form) return

    this.isSaving = true
    this.resetSavingStateAfterTimeout()

    const formData = new FormData(form)
    formData.set('auto_save', 'true')

    const saveUrl = this.urlValue || form.action

    try {
      const response = await patch(saveUrl, {
        body: formData,
        headers: { 'X-Auto-Save': 'true' }
      })

      if (!this.isConnected) return

      if (response.ok) {
        this.lastSaveTime = Date.now()
        this.hasChanges = false
        this.dispatch('saved', { detail: { timestamp: this.lastSaveTime } })
      } else {
        console.error('[AutoSave] Save failed with status:', response.statusCode)
        this.dispatch('error', { detail: { error: 'Save failed', timestamp: Date.now() } })
      }
    } catch (error) {
      if (this.isConnected) {
        console.error('[AutoSave] Save failed:', error.message)
        this.dispatch('error', { detail: { error: error.message, timestamp: Date.now() } })
      }
    } finally {
      this.isSaving = false
    }
  }

  // Manual save trigger (can be called by other controllers)
  save() {
    this.hasChanges = true // Force save
    this.saveNow()
  }

  handleBeforeUnload(event) {
    // Save synchronously on page unload if there are changes
    if (this.hasChanges && !this.isSaving) {
      const form = this.#findForm()
      if (!form) return

      // Use sendBeacon for reliable delivery during page unload
      const formData = new FormData(form)
      const data = new URLSearchParams(formData)

      const saveUrl = this.urlValue || `/application/${this.applicationIdValue}`

      // Add CSRF token and auto-save flags
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (csrfToken) {
        data.append('authenticity_token', csrfToken)
      }
      data.append('_method', 'PATCH')
      data.append('auto_save', 'true')

      try {
        navigator.sendBeacon(saveUrl, data)
      } catch (error) {
        console.warn("Failed to send auto-save beacon:", error)
      }
    }
  }

  handleVisibilityChange() {
    // Save when tab becomes hidden (user switches away)
    if (document.hidden && !this.isSaving && this.hasChanges) {
      this.saveNow()
    }
  }

  // Action methods that can be called from HTML
  saveAndExit() {
    this.hasChanges = true // Force save
    this.saveNow()
    // Let the Rails controller handle the redirect after successful save
  }

  clearAutoSaveFlag() {
    this.hasChanges = false
  }

  // Getter for last save time (for debugging)
  get lastSaveTimeFormatted() {
    return new Date(this.lastSaveTime).toLocaleTimeString()
  }

  // Private: find the form element
  #findForm() {
    return this.hasFormTarget ? this.formTarget : this.element.querySelector('form')
  }

  // Safety timeout to prevent stuck saving state
  resetSavingStateAfterTimeout(timeoutMs = 10000) {
    setTimeout(() => {
      if (this.isSaving) {
        this.isSaving = false
      }
    }, timeoutMs)
  }
}
